"use client";

import { useState, useCallback } from "react";
import { X, SlidersHorizontal, RotateCcw } from "lucide-react";
import { STYLE_CONFIG } from "@/app/components/TradingStyleBadge";
import { useT } from "@/hooks/useT";

const STYLES = Object.entries(STYLE_CONFIG)
  .filter(([key]) => key !== "MIXED")
  .map(([value, cfg]) => ({ value, label: cfg.label, color: cfg.color, bg: cfg.bg, border: cfg.border }));

export interface AdvancedFilters {
  style?: string;
  window?: string;
  min_total_pnl?: string;
  max_total_pnl?: string;
  min_volume?: string;
  max_volume?: string;
  min_trades?: string;
  max_trades?: string;
  min_roi?: string;
  max_roi?: string;
  min_profit_factor?: string;
  max_profit_factor?: string;
  min_win_rate?: string;
  max_win_rate?: string;
  min_avg_trade_usd?: string;
  max_avg_trade_usd?: string;
  min_wallet_age_days?: string;
  max_wallet_age_days?: string;
  min_entry_edge?: string;
  max_entry_edge?: string;
}

const EMPTY_FILTERS: AdvancedFilters = {};

function RangeInput({ label, minKey, maxKey, filters, onChange, ph }: {
  label: string;
  minKey: keyof AdvancedFilters;
  maxKey: keyof AdvancedFilters;
  filters: AdvancedFilters;
  onChange: (key: keyof AdvancedFilters, value: string) => void;
  ph?: [string, string];
}) {
  const t = useT();
  return (
    <div>
      <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input type="number" step="any" value={filters[minKey] || ""} onChange={(e) => onChange(minKey, e.target.value)} placeholder={ph?.[0] || t("traders.adv.range.min")}
          className="flex-1 min-w-0 bg-black/[0.03] dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.08] rounded-[5px] px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/[0.2] transition-colors" />
        <span className="text-gray-400 text-xs">—</span>
        <input type="number" step="any" value={filters[maxKey] || ""} onChange={(e) => onChange(maxKey, e.target.value)} placeholder={ph?.[1] || t("traders.adv.range.max")}
          className="flex-1 min-w-0 bg-black/[0.03] dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.08] rounded-[5px] px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/[0.2] transition-colors" />
      </div>
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  filters: AdvancedFilters;
  onApply: (filters: AdvancedFilters) => void;
}

export default function AdvancedFiltersPanel({ isOpen, onClose, filters, onApply }: Props) {
  const t = useT();
  const WINDOWS = [
    { value: "all_time", label: t("traders.adv.window.allTime") },
    { value: "30d", label: t("traders.adv.window.30d") },
    { value: "7d", label: t("traders.adv.window.7d") },
    { value: "1d", label: t("traders.adv.window.24h") },
  ];
  const [local, setLocal] = useState<AdvancedFilters>(filters);

  const handleChange = useCallback((key: keyof AdvancedFilters, value: string) => {
    setLocal(prev => ({ ...prev, [key]: value || undefined }));
  }, []);

  const activeCount = Object.values(local).filter(v => v !== undefined && v !== "").length;

  const handleApply = () => {
    const cleaned: AdvancedFilters = {};
    for (const [k, v] of Object.entries(local)) {
      if (v !== undefined && v !== "") (cleaned as any)[k] = v;
    }
    onApply(cleaned);
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
  };

  if (!isOpen) return null;

  return (
    <div className="bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] overflow-hidden shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1A1A1A]">
        <div className="flex items-center gap-2.5">
          <SlidersHorizontal className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-bold text-gray-900 dark:text-white">{t("traders.adv.title")}</span>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-bold bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-[5px]">{t("traders.adv.activeBadge", { count: activeCount })}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> {t("traders.adv.reset")}
          </button>
          <button onClick={handleApply}
            className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-bold rounded-[5px] hover:opacity-90 active:scale-[0.98] transition-all">
            {t("traders.adv.apply")}
          </button>
          <button onClick={onClose} className="p-1.5 rounded-[5px] hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 space-y-5">
        {/* Row 1: Style + Window */}
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t("traders.adv.style")}</div>
            <div className="flex flex-wrap gap-1.5">
              {STYLES.map(s => {
                const current = (local.style || "").split(",").filter(Boolean);
                const selected = current.includes(s.value);
                return (
                  <button key={s.value}
                    onClick={() => {
                      const next = selected ? current.filter(v => v !== s.value) : [...current, s.value];
                      handleChange("style", next.join(","));
                    }}
                    className={`px-3 py-1.5 text-xs font-bold rounded-[5px] border transition-colors ${
                      selected
                        ? `${s.bg} ${s.color} ${s.border}`
                        : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 border-transparent hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                    }`}
                  >{s.label}</button>
                );
              })}
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t("traders.adv.window")}</div>
            <div className="flex gap-1.5">
              {WINDOWS.map(w => (
                <button key={w.value}
                  onClick={() => handleChange("window", local.window === w.value ? "" : w.value)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-[5px] transition-colors ${
                    local.window === w.value
                      ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                      : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                  }`}
                >{w.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: Range filters — responsive grid */}
        <div>
          <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{t("traders.adv.metrics")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <RangeInput label={t("traders.adv.range.pnl")} minKey="min_total_pnl" maxKey="max_total_pnl" filters={local} onChange={handleChange} ph={["1,000", "1,000,000"]} />
            <RangeInput label={t("traders.adv.range.volume")} minKey="min_volume" maxKey="max_volume" filters={local} onChange={handleChange} ph={["10,000", "50,000,000"]} />
            <RangeInput label={t("traders.adv.range.trades")} minKey="min_trades" maxKey="max_trades" filters={local} onChange={handleChange} ph={["10", "100,000"]} />
            <RangeInput label={t("traders.adv.range.roi")} minKey="min_roi" maxKey="max_roi" filters={local} onChange={handleChange} ph={["0.05", "10"]} />
            <RangeInput label={t("traders.adv.range.pf")} minKey="min_profit_factor" maxKey="max_profit_factor" filters={local} onChange={handleChange} ph={["1.5", "100"]} />
            <RangeInput label={t("traders.adv.range.winRate")} minKey="min_win_rate" maxKey="max_win_rate" filters={local} onChange={handleChange} ph={["0.55", "0.95"]} />
            <RangeInput label={t("traders.adv.range.avgTrade")} minKey="min_avg_trade_usd" maxKey="max_avg_trade_usd" filters={local} onChange={handleChange} ph={["100", "50,000"]} />
            <RangeInput label={t("traders.adv.range.walletAge")} minKey="min_wallet_age_days" maxKey="max_wallet_age_days" filters={local} onChange={handleChange} ph={["30", "365"]} />
            <RangeInput label={t("traders.adv.range.entryEdge")} minKey="min_entry_edge" maxKey="max_entry_edge" filters={local} onChange={handleChange} ph={["-0.2", "0.5"]} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function FilterButton({ activeCount, onClick }: { activeCount: number; onClick: () => void }) {
  const t = useT();
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
        activeCount > 0
          ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
          : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
      }`}
    >
      <SlidersHorizontal className="w-3 h-3" />
      {t("traders.adv.filters")}
      {activeCount > 0 && (
        <span className="px-1.5 py-0.5 text-[9px] font-black bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-[5px] leading-none">{activeCount}</span>
      )}
    </button>
  );
}
